import {
  Grid,
  MenuItem,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import ContainerLayout from "../../../../components/container-layout/ContainerLayout";
import { ButtonPrimary } from "../../../../components/button/ButtonCustoms";
import ModalCustom from "../../../../components/modal/ModalCustom";
import NavigateListCustom from "../../../../components/navigation/NavigationListCustom";
import { routeName } from "../../../../routes/routeName";
import { NavigateFunction, useLocation, useNavigate, useParams } from "react-router-dom";
import IndexProjectForm from "../components/IndexProjectForm";
import ContainerButton from "../../../../components/button/ContainerButton";
import {
  UploadedFile,
  InputValueProjectForm,
  defualtValueProjectForm,
  ProjectSubmissionData,
} from "../components/domain";
import {
  setLoading,
  unsetLoading,
} from "../../../../store/loading/loading.slice";
import { alertError, alertSuccess } from "../../../../components/alert/swal";
import { edpexProjectAPI } from "../../../../api/edpex-project/edpex-project.api";
import { edpexFormMasterAPI } from "../../../../api/edpex-form-master/edpex-form-master.api";
import { EDPEX_PROJECT_STATUS } from "../enum";
import StepperCustom from "../../../../components/step/Stepper";
import FilterSelect from "../../../../components/select/filterSelect";
import InputTextField from "../../../../components/input/inputTextField";
import { YearsApi } from "../../../../api/yeas/years.api";
import InputDatePicker from "../../../../components/input/inputDatePicker";
import moment from "moment";
import { edpexProjectAmendmentAPI } from "../../../../api/edpex-project-amendment/edpex-project-amendment.api";
import EditCalendarOutlinedIcon from "@mui/icons-material/EditCalendarOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import SpecailEditProject from "../components/table/SpecailEditProject";
import StatusCustom from "../../../../components/status/statusCustom";
import BorderColorOutlinedIcon from "@mui/icons-material/BorderColorOutlined";
import IndicatorFormResult from "../components/IndicatorFormResult";
import AccordionUpload from "../../../../components/accordion/AccordionUpload";
import { EDPEX_PROJECT_AMENDMENT_NAME, expex_project_amendment_name_data } from "../enum/enum";
import InputTextMask from "../../../../components/input/inputTextMask";

const FormResponsibleProject = () => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up("md"));
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate: NavigateFunction = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const pathname = location.pathname

  const [reloadTrigger, setReloadTrigger] = useState(false);

  const [dataYears, setDataYears] = useState<any[]>([]);
  // Determine if we're in edit mode
  const isEditMode = Boolean(id);

  const [showModalSpecialEdit, setShowModalSpecialEdit] = useState<string>("");

  const [inputValue, setInputValue] = useState<InputValueProjectForm>(
    defualtValueProjectForm
  );

  const [inputValueSpecialEdit, setInputValueSpecialEdit] = useState<any>({
    id_edpex_project_amendment: "",
    edpex_project_amendment_name: "",
    id_edpex_project_budget: "",
    fiscal_year: "",
    fiscal_month: "",
    edpex_project_budget_amount: "",
    edpex_project_start_date: "",
    edpex_project_end_date: "",
    edpex_project_amendment_remark: "",
    edpex_project_amendment_status: "",
  });

  const handleInputChangeSpecialEdit = (key: any, value: any) => {
    setInputValueSpecialEdit((prev: any) => ({ ...prev, [key]: value }));
  };

  const fetchGetYears = async () => {
    const res = await YearsApi.getYears();
    const data = res.data;
    if (data.status < 300) {
      setDataYears(data.data);
    } else {
      setDataYears([]);
    }
  };

  useEffect(() => {
    fetchGetYears();
  }, []);

  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [projectStatusCheck, setProjectStatusCheck] = useState<string>("");

  const [step, setStep] = useState<number>(0);

  useEffect(() => {
    if (isEditMode && id) {
      loadProjectData(parseInt(id));
    }
  }, [id, isEditMode]);

  function convertFromApiFormat(apiData: any) {
    const formData: any = {
      edpex_project_indicators: [],
      edpex_project_budgets: [],
      edpex_project_responsible: [],
    };

    // Convert indicators back
    if (apiData.edpex_project_indicators) {
      formData.edpex_project_indicators = apiData.edpex_project_indicators.map(
        (indicator: any) => ({
          id: indicator.id_edpex_project_indicators
            ? indicator.id_edpex_project_indicators.toString()
            : "",
          name: indicator.edpex_project_indicators || "",
          type: indicator.edpex_project_indicators_target || "LEVEL",
          value: indicator.edpex_project_indicators_target_point || "",
          value_2: indicator.edpex_project_indicators_point || "",
          value_3: indicator.edpex_project_indicators_point_result || "",
        })
      );
    }

    // Convert budgets back
    if (apiData.edpex_project_budgets) {
      formData.edpex_project_budgets = apiData.edpex_project_budgets.map(
        (budget: any) => ({
          spending_info_id: budget.id_edpex_project_budget
            ? budget.id_edpex_project_budget.toString()
            : "",
          spending_info_month: budget.fiscal_month || "",
          spending_info_year: budget.fiscal_year || "",
          spending_info_value: budget.edpex_project_budget_amount
            ? budget.edpex_project_budget_amount.toString()
            : "0",
        })
      );
    }

    // Convert responsible persons back
    if (apiData.edpex_project_responsible) {
      formData.edpex_project_responsible =
        apiData.edpex_project_responsible.map((person: any) => ({
          id_employee: person.id_edpex_responsible || null,
          name: person.name,
          employee_no: person.employee_no,
          status: person.edpex_project_responsible_type || "PRIMARY",
        }));
    }

    return formData;
  }

  const loadProjectData = useCallback(
    async (projectId: number) => {
      try {
        setIsLoading(true);
        dispatch(setLoading());

        const response = await edpexProjectAPI.getEdpexProjectById(projectId);

        if (response.data.success && response.data.data) {
          const projectData = response.data.data;

          const formData = convertFromApiFormat(projectData);

          setProjectStatusCheck(projectData.edpex_project_status);
          // Map API data to form structure
          setInputValue({
            project_name: projectData.edpex_project_name || "",
            fiscal_year: projectData.fiscal_year ? +projectData.fiscal_year + 543 : moment().year(),
            start_date: projectData.edpex_project_old_start_date || projectData.edpex_project_start_date || "",
            end_date: projectData.edpex_project_old_end_date || projectData.edpex_project_end_date || "",
            budget: projectData.edpex_project_budget || 0,
            project_type: projectData.edpex_project_status || "",
            edpex_project_ongoing: projectData.edpex_project_ongoing,
            edpex_project_strategic_work:
              projectData.edpex_project_strategic_work,
            id_project_continue: projectData.id_edpex_project_ref || 0,

            responsible_department:
              projectData.edpex_project_department_responsible || "",
            responsible_director:
              projectData.edpex_project_user_responsible || "",
            responsible_work:
              projectData.edpex_project_division_responsible || "",

            edpex_mission: projectData.id_edpex_mission?.toString() || "",
            edpex_criteria: projectData.id_edpex_standard_point || 0,
            edpex_criteria_sub: projectData.id_edpex_standard_point_detail || 0,
            objective: projectData.edpex_project_objective || "",
            target_group: projectData.edpex_project_target_group || "",
            edpex_result: projectData.edpex_project_result || "",
            // indicatorList: projectData.edpex_project_indicators || [],
            // spendingInfo: projectData.edpex_project_budgets || [],
            // responsiblePersons: projectData.edpex_project_responsible || [],
            indicatorList: formData.edpex_project_indicators,
            spendingInfo: formData.edpex_project_budgets,
            responsiblePersons: formData.edpex_project_responsible,
            //
            description: projectData.edpex_project_remark || "",
            project_status: projectData.edpex_project_status || "",
          });

          // Load existing files with improved mapping
          if (
            projectData.edpex_project_files &&
            Array.isArray(projectData.edpex_project_files)
          ) {
            setUploadedFiles(
              projectData.edpex_project_files.map((file: any) => ({
                id: file.id,
                name: file.original_name || file.name,
                size: file.file_size || 0,
                type: file.mime_type || file.type || "",
                file_path: file.file_path || file.url,
              }))
            );
          }
        } else {
          throw new Error(response.message || t("error.loadProjectFailed"));
        }
      } catch (error: any) {
        console.error("Error loading project data:", error);
        alertError(error.message || t("error.loadProjectFailed"));
      } finally {
        setIsLoading(false);
        dispatch(unsetLoading());
      }
    },
    [dispatch, t]
  );
  // Update project
  // const handleUpdateProject = useCallback(
  //   async (data: ProjectSubmissionData) => {
  //     if (!id) throw new Error("Project ID is required for update");

  //     const response = await edpexProjectAPI.updateEdpeProject(
  //       parseInt(id),
  //       data
  //     );

  //     if (response.data.success) {
  //       alertSuccess(
  //         "แก้ไขโครงการ/กิจกรรม/งาน",
  //         "แก้ไขโครงการ/กิจกรรม/งาน เรียบร้อยแล้ว",
  //         undefined,
  //         () => {
  //           loadProjectData(parseInt(id));
  //         }
  //       );
  //     } else {
  //       alertError("แก้ไขโครงการ/กิจกรรม/งาน ไม่สำเร็จ");
  //     }
  //   },
  //   [id, navigate, t]
  // );

  // console.log('projectStatusCheck', projectStatusCheck === "COMPLETED");

  const allStep: any[] = useMemo(
    () =>
      projectStatusCheck === "COMPLETED"
        ? [
          {
            name: t("รายละเอียด"),
            isAccept: true,
            icon: <ArticleOutlinedIcon />,
            disabled: false,
          },
          {
            name: t("ประวัติแก้ไขพิเศษ"),
            isAccept: true,
            icon: <EditCalendarOutlinedIcon />,
            disabled: false,
          },
          {
            name: t("รายละเอียดสรุปผล"),
            isAccept: true,
            icon: <BorderColorOutlinedIcon />,
            disabled: false,
          },
        ]
        : [
          {
            name: t("รายละเอียด"),
            isAccept: true,
            icon: <ArticleOutlinedIcon />,
            disabled: false,
          },
          {
            name: t("ประวัติแก้ไขพิเศษ"),
            isAccept: true,
            icon: <EditCalendarOutlinedIcon />,
            disabled: false,
          },
        ],
    [t, projectStatusCheck]
  );

  const monthOptions = [
    { name: "มกราคม", value: "1" },
    { name: "กุมภาพันธ์", value: "2" },
    { name: "มีนาคม", value: "3" },
    { name: "เมษายน", value: "4" },
    { name: "พฤษภาคม", value: "5" },
    { name: "มิถุนายน", value: "6" },
    { name: "กรกฎาคม", value: "7" },
    { name: "สิงหาคม", value: "8" },
    { name: "กันยายน", value: "9" },
    { name: "ตุลาคม", value: "10" },
    { name: "พฤศจิกายน", value: "11" },
    { name: "ธันวาคม", value: "12" },
  ];

  const handleSubmitEditSpecial = async () => {
    try {
      dispatch(setLoading());

      const body: any =
        inputValueSpecialEdit.edpex_project_amendment_name ===
          "ขอปรับเปลี่ยนระยะเวลาเริ่มต้นดำเนินการระยะเวลาสิ้นสุดดำเนินการ"
          ? {
            edpex_project_amendment_name:
              inputValueSpecialEdit.edpex_project_amendment_name,
            id_edpex_project: id,
            edpex_project_start_date:
              inputValueSpecialEdit.edpex_project_start_date,
            edpex_project_end_date:
              inputValueSpecialEdit.edpex_project_end_date,
            edpex_project_amendment_remark:
              inputValueSpecialEdit.edpex_project_amendment_remark,
          }
          : {
            edpex_project_amendment_name:
              inputValueSpecialEdit.edpex_project_amendment_name,
            id_edpex_project: id,
            id_edpex_project_budget:
              inputValueSpecialEdit.id_edpex_project_budget,
            fiscal_year: inputValueSpecialEdit.fiscal_year - 543,
            fiscal_month: inputValueSpecialEdit.fiscal_month,
            edpex_project_budget_amount:
              inputValueSpecialEdit.edpex_project_budget_amount,
            edpex_project_amendment_remark:
              inputValueSpecialEdit.edpex_project_amendment_remark,
          };

      if (inputValueSpecialEdit.id_edpex_project_amendment) {
        const res = await edpexProjectAmendmentAPI.updateEdpeProject(
          +inputValueSpecialEdit.id_edpex_project_amendment,
          body
        );

        if (res.data.status < 300) {
          alertSuccess(
            "ส่งคำร้องขอแก้ไขพิเศษ",
            "ส่งคำร้องขอแก้ไขพิเศษ เรียบร้อยแล้ว",
            undefined,
            () => {
              if (id) {
                setReloadTrigger((prev) => !prev);
                loadProjectData(parseInt(id));
                setShowModalSpecialEdit("");
              }
            }
          );
        }
      } else {
        const res = await edpexProjectAmendmentAPI.createEdpexProjecAmendmentt(
          body
        );

        if (res.data.status < 300) {
          alertSuccess(
            "ส่งคำร้องขอแก้ไขพิเศษ",
            "ส่งคำร้องขอแก้ไขพิเศษ เรียบร้อยแล้ว",
            undefined,
            () => {
              if (id) {
                setReloadTrigger((prev) => !prev);
                loadProjectData(parseInt(id));
                setShowModalSpecialEdit("");
              }
            }
          );
        }
      }
    } catch (error: any) {
      alertError(t("เกิดข้อผิดพลาด"));
    } finally {
      dispatch(unsetLoading());
    }
  };

  useEffect(() => {
    if (showModalSpecialEdit === "") {
      setInputValueSpecialEdit({
        id_edpex_project_amendment: "",
        edpex_project_amendment_name: "",
        id_edpex_project_budget: "",
        fiscal_year: "",
        fiscal_month: "",
        edpex_project_budget_amount: "",
        edpex_project_start_date: "",
        edpex_project_end_date: "",
        edpex_project_amendment_remark: "",
        edpex_project_amendment_status: "",
      });
    }
  }, [showModalSpecialEdit]);

  return (
    <>
      <ContainerLayout
        title={t("รายละเอียด")}
        hidden_sub_title
        sub_title_custom={
          <>
            {/* <Box
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
              onClick={() => {
                setShowModal(true);
              }}
            >
              <InfoIcon color="disabled" />
              <Typography>ข้อมูล</Typography>
            </Box> */}
          </>
        }
        navigateContent={
          <NavigateListCustom
            navigateData={[
              {
                key: "1",
                name: t("ระบบ Edpex"),
                disabled_match: true,
                pathName: routeName.cms.edpexResultDisplay,
              },
              {
                key: "2",
                name: t("โครงการ/กิจกรรม/งานที่รับผิดชอบ"),
                pathName: pathname
              },
            ]}
          />
        }
        buttonContent={
          <ContainerButton
            justifyContent="flex-end"
            buttonData={
              id && projectStatusCheck === EDPEX_PROJECT_STATUS.PROCESS
                ? [
                  {
                    buttonProps: {
                      onClick: () => {
                        navigate(-1);
                      },
                    },
                    key: "CANCEL",
                    label: t("ยกเลิก"),
                    seq: 1,
                    type: "CANCEL",
                  },
                  {
                    buttonProps: {
                      onClick: () => {
                        setShowModalSpecialEdit("EDIT");
                      },
                    },
                    key: "EDIT",
                    label: t("แก้ไขพิเศษ"),
                    seq: 2,
                    type: "EDIT",
                  },
                  {
                    buttonProps: {
                      onClick: () => {
                        navigate(
                          routeName.cms.edpexResponsibleProjectsComplete.replace(
                            ":id",
                            id
                          )
                        );
                      },
                    },
                    key: "ADD2",
                    label: t("เสร็จสิ้นโครงการ/กิจกรรม/งาน"),
                    seq: 3,
                    type: "ADD2",
                  },
                ]
                : [
                  {
                    buttonProps: {
                      onClick: () => {
                        navigate(-1);
                      },
                    },
                    key: "CANCEL",
                    label: t("ยกเลิก"),
                    seq: 1,
                    type: "CANCEL",
                  },
                ]
            }
          />
        }
        content={
          <>
            <section className="flex items-center justify-center">
              <StepperCustom
                sx={{ maxWidth: "1200px" }}
                step={step}
                setStep={(num: number) => {
                  setStep(num);
                }}
                stepLabel={allStep}
                view={true}
              />
            </section>

            {step === 0 && (
              <section className="w-full mt-10">
                <IndexProjectForm
                  inputValue={inputValue}
                  setInputValue={setInputValue}
                  uploadedFiles={uploadedFiles}
                  setUploadedFiles={setUploadedFiles}
                  disable={!!id && projectStatusCheck !== "DRAFT"}
                />
              </section>
            )}

            {step === 1 && (
              <section className="w-full mt-10">
                <SpecailEditProject
                  isApprove={false}
                  reloadTrigger={reloadTrigger}
                  setShowModalSpecialEdit={setShowModalSpecialEdit}
                  setInputValueSpecialEdit={setInputValueSpecialEdit}
                />
              </section>
            )}

            {step === 2 && (
              <section className="w-full mt-10">
                <IndicatorFormResult
                  inputValue={inputValue}
                  setInputValue={setInputValue}
                  disabled={true}
                  type={projectStatusCheck}
                />

                <br />
                <AccordionUpload
                  defaultExpanded
                  required
                  enableButtonLeft
                  titleName="ไฟล์เอกสาร"
                  uploadedFiles={uploadedFiles}
                  setUploadedFiles={setUploadedFiles}
                  acceptedFileTypes=".pdf,.doc,.docx,.jpg,.png,.xlsx"
                  maxFileSize={5} // 5MB limit
                  multiple={true}
                  isView={true}
                // onFileUpload={handleFileUpload} // Pass the upload handler if needed by component
                // onRemoveFile={handleRemoveFile} // Pass the remove handler if needed
                />
              </section>
            )}
          </>
        }
      />

      {showModalSpecialEdit && (
        <ModalCustom
          open
          title={"ส่งคำร้องขอแก้ไขพิเศษ"}
          subTitle={<div className="flex gap-4 justify-center items-center">
            {inputValueSpecialEdit.edpex_project_amendment_status ? (
              <StatusCustom
                disableMaxWidth
                sx={{ maxWidth: "fit-content", px: 2.5 }}
                text={
                  inputValueSpecialEdit.edpex_project_amendment_status ===
                    "PENDING"
                    ? "รอการอนุมัติ"
                    : inputValueSpecialEdit.edpex_project_amendment_status ===
                      "APPROVE"
                      ? "อนุมัติแล้ว"
                      : inputValueSpecialEdit.edpex_project_amendment_status ===
                        "REJECT"
                        ? "ไม่อนุมัติ"
                        : ""
                }
                type={
                  inputValueSpecialEdit.edpex_project_amendment_status ===
                    "PENDING"
                    ? "warning"
                    : inputValueSpecialEdit.edpex_project_amendment_status ===
                      "APPROVE"
                      ? "approve"
                      : inputValueSpecialEdit.edpex_project_amendment_status ===
                        "REJECT"
                        ? "reject"
                        : ""
                }
              />
            ) : "กรุณาระบุข้อมูลให้ครบถ้วนก่อนทำการยืนยัน"}
            {inputValueSpecialEdit.edpex_project_amendment_status ===
              "REJECT" && (
                <Typography color="error">
                  หมายเหตุ :{" "}
                  {inputValueSpecialEdit?.edpex_project_amendment_remark_cancel}
                </Typography>
              )}

          </div>}
          onClose={() => {
            setShowModalSpecialEdit("");
          }}
          component={
            <div className="w-full">


              <section className="w-full mt-10">
                <Grid container spacing={2}>
                  <Grid
                    item
                    xs={12}
                    md={12}
                    lg={12}
                    sx={{
                      alignSelf: "end",
                    }}
                  >
                    <FilterSelect
                      value={
                        inputValueSpecialEdit.edpex_project_amendment_name ?? ""
                      }
                      labelId="ข้อมูลที่ต้องการแก้ไข-id"
                      selectId="ข้อมูลที่ต้องการแก้ไข-select-id"
                      heading="ข้อมูลที่ต้องการแก้ไข"
                      options={expex_project_amendment_name_data?.map((list) => ({ name: list.label, value: list.value })).map((item: any, index: number) => (
                        <MenuItem value={item?.value} key={index + 1}>
                          {item?.name}
                        </MenuItem>
                      ))}
                      required
                      disabled={showModalSpecialEdit === "VIEW"}
                      onchange={(e: any) => {
                        handleInputChangeSpecialEdit(
                          "edpex_project_amendment_name",
                          e.target.value
                        );
                      }}
                    />
                  </Grid>
                  {/*  */}
                  {inputValueSpecialEdit.edpex_project_amendment_name ===
                    "ขอปรับเปลี่ยนระยะเวลาเริ่มต้นดำเนินการระยะเวลาสิ้นสุดดำเนินการ" ? (
                    <>
                      <Grid
                        item
                        xs={12}
                        md={12}
                        lg={12}
                        sx={{
                          alignSelf: "end",
                        }}
                      >
                        ระยะเวลาเดิม
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        md={6}
                        lg={6}
                        sx={{
                          alignSelf: "end",
                        }}
                      >
                        <InputDatePicker
                          heading={"ระยะเวลาเริ่มต้นดำเนินการ"}
                          placeholder={"ระยะเวลาเริ่มต้นดำเนินการ"}
                          value={inputValue.start_date ?? ""}
                          onChange={(e: any) => { }}
                          helperText={""}
                          disabled={true}
                        />
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        md={6}
                        lg={6}
                        sx={{
                          alignSelf: "end",
                        }}
                      >
                        <InputDatePicker
                          heading={"ระยะเวลาสิ้นสุดดำเนินการ"}
                          placeholder={"ระยะเวลาสิ้นสุดดำเนินการ"}
                          value={inputValue.end_date ?? ""}
                          onChange={(e: any) => { }}
                          helperText={""}
                          disabled={true}
                        />
                      </Grid>
                      {/*  */}
                      <Grid
                        item
                        xs={12}
                        md={12}
                        lg={12}
                        sx={{
                          alignSelf: "end",
                        }}
                      >
                        ระยะเวลาที่ต้องการแก้ไข
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        md={6}
                        lg={6}
                        sx={{
                          alignSelf: "end",
                        }}
                      >
                        <InputDatePicker
                          heading={"ระยะเวลาเริ่มต้นดำเนินการ"}
                          placeholder={"ระยะเวลาเริ่มต้นดำเนินการ"}
                          value={
                            inputValueSpecialEdit.edpex_project_start_date ?? ""
                          }
                          onChange={(e: any) =>
                            handleInputChangeSpecialEdit(
                              "edpex_project_start_date",
                              moment(e).format("YYYY-MM-DD")
                            )
                          }
                          helperText={""}
                          disabled={showModalSpecialEdit === "VIEW"}
                          disableStartDate={
                            inputValue.fiscal_year - 544 + "-10-01"
                          }
                          disableEndDate={
                            inputValue.fiscal_year - 543 + "-09-30"
                          }
                        />
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        md={6}
                        lg={6}
                        sx={{
                          alignSelf: "end",
                        }}
                      >
                        <InputDatePicker
                          heading={"ระยะเวลาสิ้นสุดดำเนินการ"}
                          placeholder={"ระยะเวลาสิ้นสุดดำเนินการ"}
                          value={
                            inputValueSpecialEdit.edpex_project_end_date ?? ""
                          }
                          onChange={(e: any) =>
                            handleInputChangeSpecialEdit(
                              "edpex_project_end_date",
                              moment(e).format("YYYY-MM-DD")
                            )
                          }
                          disableStartDate={
                            inputValue.fiscal_year - 544 + "-10-01"
                          }
                          disableEndDate={
                            inputValue.fiscal_year - 543 + "-09-30"
                          }
                          helperText={""}
                          disabled={showModalSpecialEdit === "VIEW"}
                        />
                      </Grid>

                      <Grid
                        item
                        xs={12}
                        md={12}
                        lg={12}
                        sx={{
                          alignSelf: "end",
                        }}
                      >
                        <InputTextField
                          heading={"หมายเหตุ"}
                          placeholder={"หมายเหตุ"}
                          value={
                            inputValueSpecialEdit.edpex_project_amendment_remark ??
                            ""
                          }
                          onchange={(e: any) =>
                            handleInputChangeSpecialEdit(
                              "edpex_project_amendment_remark",
                              e.target.value
                            )
                          }
                          disabled={showModalSpecialEdit === "VIEW"}
                          helperText={""}
                          row={3}
                          maxRows={5}
                          disablePaddingMultiline
                          multiline
                          inputHeight={"auto"}
                        />
                      </Grid>
                    </>
                  ) : inputValueSpecialEdit.edpex_project_amendment_name ===
                    "ขอปรับเปลี่ยนข้อมูลการใช้เงิน" ? (
                    <>
                      <Grid
                        item
                        xs={12}
                        md={12}
                        lg={12}
                        sx={{
                          alignSelf: "end",
                        }}
                      >
                        <FilterSelect
                          value={
                            inputValueSpecialEdit.id_edpex_project_budget ?? ""
                          }
                          labelId="งวดเงินที่ต้องการแก้ไข-id"
                          selectId="งวดเงินที่ต้องการแก้ไข-select-id"
                          heading="งวดเงินที่ต้องการแก้ไข"
                          options={inputValue.spendingInfo.map(
                            (item: any, index: number) => (
                              <MenuItem
                                value={item?.spending_info_id}
                                key={index + 1}
                              >
                                {`เดือน ${item?.spending_info_month} ปีงบประมาณ ${item?.spending_info_year} (จำนวนเงิน : ${item?.spending_info_value})`}
                              </MenuItem>
                            )
                          )}
                          required
                          disabled={showModalSpecialEdit === "VIEW"}
                          onchange={(e: any) => {
                            handleInputChangeSpecialEdit(
                              "id_edpex_project_budget",
                              e.target.value
                            );
                          }}
                        />
                      </Grid>

                      <Grid
                        item
                        xs={12}
                        md={12}
                        lg={12}
                        sx={{
                          alignSelf: "end",
                        }}
                      >
                        แก้ไขเป็น
                      </Grid>
                      {/*  */}
                      <Grid
                        item
                        xs={12}
                        md={4}
                        lg={4}
                        sx={{
                          alignSelf: "end",
                        }}
                      >
                        <FilterSelect
                          value={inputValueSpecialEdit.fiscal_month ?? ""}
                          labelId="เดือนที่ใช้-id"
                          selectId="เดือนที่ใช้-select-id"
                          heading="เดือนที่ใช้"
                          required
                          options={[...monthOptions].map(
                            (item: any, index: number) => (
                              <MenuItem value={item?.value} key={index + 1}>
                                {item?.name}
                              </MenuItem>
                            )
                          )}
                          disabled={showModalSpecialEdit === "VIEW"}
                          onchange={(e: any) => {
                            handleInputChangeSpecialEdit(
                              "fiscal_month",
                              e.target.value
                            );
                          }}
                        />
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        md={4}
                        lg={4}
                        sx={{
                          alignSelf: "end",
                        }}
                      >
                        <InputTextMask
                          mode="amount"
                          heading={"จำนวนเงิน"}
                          placeholder={"จำนวนเงิน"}
                          value={
                            inputValueSpecialEdit.edpex_project_budget_amount ??
                            ""
                          }
                          onChange={(e) =>
                            handleInputChangeSpecialEdit(
                              "edpex_project_budget_amount",
                              e.target.value
                            )
                          }
                          required
                          helperText={""}
                          disabled={showModalSpecialEdit === "VIEW"}
                        />
                      </Grid>

                      <Grid
                        item
                        xs={12}
                        md={4}
                        lg={4}
                        sx={{
                          alignSelf: "end",
                        }}
                      >
                        <FilterSelect
                          value={inputValueSpecialEdit.fiscal_year ?? ""}
                          labelId="ทุกปีงบปะมาณ-id"
                          selectId="ทุกปีงบปะมาณ-select-id"
                          heading="ปีงบประมาณ"
                          required
                          options={dataYears?.map(
                            (item: any, index: number) => (
                              <MenuItem value={item?.year} key={index + 1}>
                                {item?.year}
                              </MenuItem>
                            )
                          )}
                          onchange={(e: any) =>
                            handleInputChangeSpecialEdit(
                              "fiscal_year",
                              e.target.value
                            )
                          }
                          disabled={showModalSpecialEdit === "VIEW"}
                        />
                      </Grid>

                      <Grid
                        item
                        xs={12}
                        md={12}
                        lg={12}
                        sx={{
                          alignSelf: "end",
                        }}
                      >
                        <InputTextField
                          heading={"หมายเหตุ"}
                          placeholder={"หมายเหตุ"}
                          value={
                            inputValueSpecialEdit.edpex_project_amendment_remark ??
                            ""
                          }
                          onchange={(e: any) =>
                            handleInputChangeSpecialEdit(
                              "edpex_project_amendment_remark",
                              e.target.value
                            )
                          }
                          helperText={""}
                          row={3}
                          maxRows={5}
                          disablePaddingMultiline
                          multiline
                          inputHeight={"auto"}
                          disabled={showModalSpecialEdit === "VIEW"}
                        />
                      </Grid>
                    </>
                  ) : null}
                </Grid>
              </section>

              <div className="mt-6 w-full flex justify-center gap-4 items-center">
                <span>
                  <ButtonPrimary
                    mode="cancel"
                    buttonName={t("BUTTON.CANCEL")}
                    onClick={() => {
                      setShowModalSpecialEdit("");
                    }}
                  />

                </span>
                <span>
                  {showModalSpecialEdit !== "VIEW" && (
                    <ButtonPrimary
                      mode="next"
                      buttonName={t("ยืนยัน")}
                      disabled={(
                        (!inputValueSpecialEdit.edpex_project_budget_amount ||
                          !inputValueSpecialEdit.id_edpex_project_budget ||
                          !inputValueSpecialEdit.fiscal_month ||
                          !inputValueSpecialEdit.fiscal_year
                        )
                        && inputValueSpecialEdit.edpex_project_amendment_name === EDPEX_PROJECT_AMENDMENT_NAME.BUDGET
                      )}
                      onClick={() => {
                        // เช็ค ยอดเงิน ห้ามเกินยอดงบประมาณที่เลือก
                        // console.log(inputValue?.spendingInfo,'inputValue?.spendingInfo')
                        const budget_all = Number((inputValue?.spendingInfo as any[])?.find((x) => x?.spending_info_id == inputValueSpecialEdit?.id_edpex_project_budget)?.spending_info_value || 0)
                        // console.log(budget_all, 'budget_all')
                        if ((Number(inputValueSpecialEdit.edpex_project_budget_amount) > budget_all) && inputValueSpecialEdit.edpex_project_amendment_name === EDPEX_PROJECT_AMENDMENT_NAME.BUDGET) {
                          return alertError("ยอดเงินที่ระบุเกินยอดงบประมาณที่ตั้งไว้")
                        }
                        handleSubmitEditSpecial();
                      }}
                    />
                  )}
                </span>
              </div>
            </div>
          }
        />
      )}
    </>
  );
};

export default FormResponsibleProject;
